import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { clone } from 'src/app/common/utils/object.extensions';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import * as PlaceQueryCommandActions from 'src/app/modules/place-calendar/ngrx.stores/place.query.command/actions';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { FancyPlaceDetailsModalComponent } from '../place.details.modal/fancy.place.details.modal/fancy.place.details.modal.component';
import { IPlaceDetailsModalData } from '../place.details.modal/place.details.modal.provider';
import { placeQueryCommandSelector } from '../../ngrx.stores/place.query.command/selectors';
import { ConfigService } from 'src/app/common/services/config/config.service';
import {
  IMapMarkerWithCustomData,
  IPlaceAndMarker,
} from 'src/app/modules/shared/models/place.marker';

@Component({
  selector: 'ig-visitor-place-map',
  templateUrl: './visitor.place.map.component.html',
  styleUrls: ['./visitor.place.map.component.scss'],
})
export class VisitorPlaceMapComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  placesAndMarkers: IPlaceAndMarker;
  allPlaces: IPlaceAndMarker;
  eventsRowScrolllistener: () => void = null;
  isShowDetails = false;
  showingPlace: VisitorPlace = null;
  mapCenter: { lat: number; lng: number } = null;
  isLoading = true;

  showingCount = '1 - 20';
  showingFrom = 0;
  showingTo = 19;
  paging = 20;
  isAtStart = true;
  isAtEnd = false;

  @ViewChild('eventsRow', { static: false }) eventsRow: ElementRef;
  @ViewChild(GoogleMapComponent, { static: false }) map: GoogleMapComponent;
  @ViewChildren('event') eventEles: QueryList<ElementRef>;

  constructor(
    public activatedRoute: ActivatedRoute,
    public modalService: ModalService,
    private _placeService: VisitorPlaceService,
    private _renderer2: Renderer2,
    private _store: Store<IAppState>
  ) {
    super({ activatedRoute });
  }

  ngOnInit(): void {
    // this.clearFilters();
    let { category } = this.allRouteParams;
    let categoryList = [];

    // check if the id is a fav/custom place category or a custom default place category
    if (category) {
      let customCategory = this._placeService.findCategoryById(
        category,
        this.frontendSettings.customSettings.customDefaultPlaceCategory
          .customCategories
      );
      if (customCategory) {
        this._placeService.getAllSubCategories(customCategory, categoryList);
        this._store.dispatch(
          PlaceQueryCommandActions.setCustomDefaultCategoryList({
            categoryList: categoryList,
            categoryName: customCategory.title,
          })
        );
      } else {
        const fav = this.frontendSettings.customSettings.customPlaces.find(
          (x) => x._id.toString() === category
        );

        this._store.dispatch(
          PlaceQueryCommandActions.setFavoritePlaceBtnId({
            favBtnId: fav._id,
            hasFavoriteCategoryFromUrl: null,
          })
        );
      }
    }

    this._store
      .pipe(takeUntil(this.isDestroyed), select(placeQueryCommandSelector))
      .subscribe((cmd) => {
        this._placeService
          .getPlaces(this.hubName, { ...cmd, limit: 1000, isForMapView: true })
          .pipe(
            switchMap((places) =>
              this.createMapMarkersForPlaces$(places).pipe(
                map((markers) => ({ places, markers }))
              )
            )
          )
          .subscribe((data) => {
            this.allPlaces = data;
            const paging =
              data.places.length > this.paging
                ? this.paging
                : data.places.length;
            this.pagingPlaces(data, paging);
            if (!this.allPlaces.places.length) {
              this.map?.setNoContent();
            }
            this.isLoading = false;
          });
      });
  }

  showDetails(place: VisitorPlace) {
    this.isShowDetails = true;
    // this.showingPlace = place;

    this.modalService.show({
      component: FancyPlaceDetailsModalComponent,
      panelClass: 'ig-modal-w-full',
      data: {
        currentPlace: place,
      } as IPlaceDetailsModalData,
    });

    this.mapCenter = {
      lat: place.address.lat,
      lng: place.address.lng,
    };

    this.placesAndMarkers.markers.forEach((x) => {
      if (x.title !== place._id) {
        x.options = {
          icon: null,
          animation: null,
          zIndex: 0,
        };
      } else {
        x.options = {
          icon: 'https://iti-images.s3.amazonaws.com/imgs/marker-xs.png',
          animation: google.maps.Animation.BOUNCE,
          zIndex: 2,
        };
      }
    });
  }

  pagingPlaces(data: IPlaceAndMarker, paging) {
    const items = clone(data);
    const places = items.places.slice(
      this.showingFrom,
      this.showingFrom + paging
    );
    const markers = items.markers.slice(
      this.showingFrom,
      this.showingFrom + paging
    );
    this.placesAndMarkers = { places, markers };
    this.checkCursor();
  }

  goLeft() {
    if (this.isAtStart) return;

    this.showingTo = this.showingFrom - 1;
    const paging =
      this.showingFrom - this.paging < 0 ? this.showingFrom : this.paging;

    this.showingFrom -= paging;
    this.showingCount = `${this.showingFrom + 1} - ${this.showingTo + 1}`;
    this.checkCursor();
    this.pagingPlaces(this.allPlaces, paging);
  }

  goRight() {
    if (this.isAtEnd) return;

    this.showingFrom = this.showingTo + 1;

    const paging =
      this.showingTo + this.paging > this.allPlaces.places.length
        ? this.allPlaces.places.length - this.showingTo
        : this.paging;

    this.showingTo += paging;
    this.showingCount = `${this.showingFrom + 1} - ${this.showingTo + 1}`;
    this.checkCursor();
    this.pagingPlaces(this.allPlaces, paging);
  }

  checkCursor() {
    this.isAtStart = this.showingFrom <= 0;
    this.isAtEnd = this.showingTo >= this.allPlaces.places.length;
  }

  markerClicked(marker: IMapMarkerWithCustomData<VisitorPlace>) {
    this.showDetails(marker.data);
    // this.eventEleScrollIntoView(marker);
  }

  eventEleScrollIntoView(marker: IMapMarkerWithCustomData<VisitorPlace>) {
    const eventEle = this.eventEles.find(
      (x) => x.nativeElement.attributes['data-id'].value === marker.data._id
    );
    eventEle &&
      eventEle.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
  }

  createMapMarkersForPlaces$(
    places: VisitorPlace[]
  ): Observable<IMapMarkerWithCustomData<VisitorPlace>[]> {
    return of(
      places.map((e) => {
        return {
          position: {
            lat: e.address?.lat,
            lng: e.address?.lng,
          },
          title: e._id,
          data: e,
        } as IMapMarkerWithCustomData<VisitorPlace>;
      })
    );
  }

  clearFilters() {
    this._store.dispatch(
      PlaceQueryCommandActions.setCustomDefaultCategoryList({
        categoryList: null,
        categoryName: null,
        categoryNode: null,
      })
    );
    this._store.dispatch(
      PlaceQueryCommandActions.setFavoritePlaceBtnId({
        favBtnId: null,
        hasFavoriteCategoryFromUrl: null,
      })
    );
    this._store.dispatch(
      PlaceQueryCommandActions.setLocation({
        // feedId: null,
        location: null,
      })
    );
  }

  // Add or update these methods in your VisitorPlaceMapComponent class

  goLeftBy1Place() {
    if (!this.eventsRow) return;

    // Find the scrollContainer element
    const scrollContainer =
      this.eventsRow.nativeElement.querySelector('#scrollContainer') ||
      this.eventsRow.nativeElement;

    // Get event elements
    const eventElements = this.eventEles?.toArray() || [];
    if (eventElements.length === 0) return;

    // Calculate the width of a single card (including gap)
    // Your HTML shows you have gap-8 (2rem) between items
    const itemWidth = eventElements[0].nativeElement.offsetWidth + 32; // width + gap

    // Scroll left by one item width
    scrollContainer.scrollLeft -= itemWidth;

    // Update scroll indicators
    setTimeout(() => this.updateScrollIndicators(), 100);
  }

  goRightBy1Place() {
    if (!this.eventsRow) return;

    // Find the scrollContainer element
    const scrollContainer =
      this.eventsRow.nativeElement.querySelector('#scrollContainer') ||
      this.eventsRow.nativeElement;

    // Get event elements
    const eventElements = this.eventEles?.toArray() || [];
    if (eventElements.length === 0) return;

    // Calculate the width of a single card (including gap)
    const itemWidth = eventElements[0].nativeElement.offsetWidth + 32; // width + gap

    // Scroll right by one item width
    scrollContainer.scrollLeft += itemWidth;

    // Update scroll indicators
    setTimeout(() => this.updateScrollIndicators(), 100);
  }

  // Method to update scroll indicators
  updateScrollIndicators() {
    if (!this.eventsRow) return;

    // Find the scrollContainer element
    const scrollContainer =
      this.eventsRow.nativeElement.querySelector('#scrollContainer') ||
      this.eventsRow.nativeElement;

    const scrollLeft = scrollContainer.scrollLeft;
    const scrollWidth = scrollContainer.scrollWidth;
    const clientWidth = scrollContainer.clientWidth;

    // Determine if at start or end of scroll
    this.isAtStart = scrollLeft <= 0;
    this.isAtEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 5; // 5px buffer for rounding errors
  }

  // Add this to ngAfterViewInit (or create it if it doesn't exist)
  ngAfterViewInit() {
    // Wait for view to initialize completely
    setTimeout(() => {
      this.initScrollListener();
      this.updateScrollIndicators();
    }, 100);
  }

  // Initialize scroll event listener
  initScrollListener() {
    if (this.eventsRow && !this.eventsRowScrolllistener) {
      // Find the scrollContainer element
      const scrollContainer =
        this.eventsRow.nativeElement.querySelector('#scrollContainer') ||
        this.eventsRow.nativeElement;

      // Add scroll event listener
      this.eventsRowScrolllistener = this._renderer2.listen(
        scrollContainer,
        'scroll',
        () => this.updateScrollIndicators()
      );
    }
  }

  // Make sure to clean up in ngOnDestroy
  override ngOnDestroy() {
    // Remove the scroll listener if it exists
    if (this.eventsRowScrolllistener) {
      this.eventsRowScrolllistener();
      this.eventsRowScrolllistener = null;
    }

    // Call parent destroy if it exists
    super.ngOnDestroy();
  }
}
